/**
 * Quand remettre une image reçue au décodeur, et quelle date écrire sur une
 * trame qui part.
 *
 * Toutes les heures de ce fichier sont sur l'horloge « commune » d'un contexte
 * JavaScript, `performance.timeOrigin + performance.now()` : c'est la seule
 * qu'une page et son worker partagent, leurs `performance.now()` n'ayant pas la
 * même origine.
 */

export interface VideoHold {
  /**
   * À ajouter à l'horloge commune pour obtenir l'instant de séance, en ms.
   * `null` tant que l'écart n'est pas mesuré.
   */
  epochOffsetMs: number | null;
  /** Combien retenir une image derrière son instant de prise, en ms. Zéro : elle passe tout de suite. */
  holdMs: number;
  /**
   * Ce que le décodage et l'affichage ajoutent après la remise d'une trame, en
   * ms, retranché pour viser l'écran plutôt que le décodeur. Mesuré sur ce
   * poste, image par image (`observeDisplayedFrames`) : il va de quelques
   * dizaines de millisecondes sur un appareil qui décode en matériel à
   * plusieurs centaines sur une machine qui décode en logiciel.
   */
  outputLatencyMs: number;
}

/** La latence de sortie qu'on suppose tant qu'aucune image n'a été mesurée. */
export const OUTPUT_LATENCY_MS = 30;

/**
 * Le trajet qu'on prête à une image sans date, en ms : celle d'une source qui
 * ne sait pas dater. Faute de mieux, on la retient depuis son arrivée.
 */
export const UNSTAMPED_TRANSIT_MS = 250;

/**
 * Le plus longtemps qu'une image est retenue, en ms. Une date aberrante — une
 * horloge fausse — ne doit pas garder des images indéfiniment.
 */
export const MAX_HOLD_MS = 10_000;

/**
 * L'heure commune à laquelle remettre une trame arrivée à `arrivalMs`.
 *
 * Datée et horloge mesurée : l'instant de séance de sa prise plus le retard,
 * ramené sur l'horloge commune. Déjà passé, elle part tout de suite : on ne
 * rattrape pas un retard.
 */
export function releaseAt(hold: VideoHold, stampSessionMs: number | null, arrivalMs: number): number {
  const holdMs = Math.min(MAX_HOLD_MS, Math.max(0, hold.holdMs));

  if (holdMs === 0) {
    return arrivalMs;
  }

  const outputMs = Math.max(0, hold.outputLatencyMs);

  if (stampSessionMs === null || hold.epochOffsetMs === null) {
    return arrivalMs + Math.max(0, holdMs - UNSTAMPED_TRANSIT_MS - outputMs);
  }

  const due = stampSessionMs + holdMs - outputMs - hold.epochOffsetMs;

  return Math.min(Math.max(due, arrivalMs), arrivalMs + MAX_HOLD_MS);
}

/**
 * Au-delà, l'instant de capture qu'une trame annonce ne sert pas de date, en
 * ms : une image met quelques dizaines de millisecondes de la caméra à
 * l'encodeur, pas une demi-seconde, et une valeur plus vieille dit plutôt une
 * horloge qu'on lirait de travers.
 */
export const MAX_CAPTURE_AGE_MS = 500;

/** D'où l'on tient l'instant de capture d'une image qui part. */
export interface CaptureHints {
  /** L'origine de temps du contexte qui lit la trame. */
  timeOrigin: number;
  /** L'instant de capture donné par le navigateur, relatif à `timeOrigin`. */
  captureTime?: number;
  /** L'instant retrouvé par l'horodatage RTP, sur l'horloge commune (`CaptureClock`). */
  estimatedMs?: number | null;
}

/**
 * L'instant de séance à écrire sur une image qui part.
 *
 * Celui de sa **capture** : c'est lui qui répond au geste, puisqu'une note
 * porte l'instant où la touche a été reçue. Le navigateur le donne parfois
 * (`captureTime` : Chromium 148 le donne pour le son, pas pour l'image) ; sinon
 * il est retrouvé par l'horodatage RTP. Une valeur hors de la plage plausible
 * est ignorée plutôt que crue, et la trame est datée à l'instant présent, à la
 * sortie de l'encodeur : en retard sur la scène de la durée d'encodage.
 */
export function stampTime(nowMs: number, epochOffsetMs: number, hints: CaptureHints): number {
  const candidates = [
    hints.captureTime === undefined ? null : hints.timeOrigin + hints.captureTime,
    hints.estimatedMs ?? null,
  ];

  for (const captured of candidates) {
    if (captured === null || !Number.isFinite(captured)) {
      continue;
    }

    const age = nowMs - captured;

    if (age >= 0 && age <= MAX_CAPTURE_AGE_MS) {
      return captured + epochOffsetMs;
    }
  }

  return nowMs + epochOffsetMs;
}

/** La durée d'une trame de son, en ms : Opus en WebRTC, sous Chromium comme sous Safari. */
export const AUDIO_FRAME_MS = 20;

/**
 * L'instant de prise d'un son retrouvé par l'horodatage RTP, ramené au début de
 * sa trame.
 *
 * L'horodatage RTP d'une trame de son est celui de son premier échantillon, et
 * une trame ne sort de l'encodeur qu'une fois pleine : même la plus rapide sort
 * une trame après sa prise, et l'ancre de `CaptureClock` est en retard d'autant.
 * On le retranche, pour dater le son là où on l'entend commencer — là où le
 * date `captureTime`, que Chromium donne pour le son.
 */
export function audioFrameStart(estimatedMs: number | null): number | null {
  if (estimatedMs === null) {
    return null;
  }

  return estimatedMs - AUDIO_FRAME_MS;
}

/**
 * Les trames en attente, rendues dans leur ordre d'arrivée.
 *
 * Une trame n'est jamais remise avant celle qui la précède : le décodeur
 * attend les images d'un groupe dans l'ordre, et une échéance qui recule —
 * un délai qui baisse de quelques millisecondes — ne doit pas en faire passer
 * une devant l'autre. Elle se range derrière la précédente.
 */
export class ReleaseQueue<T> {
  private readonly waiting: { at: number; item: T }[] = [];
  private lastAt = Number.NEGATIVE_INFINITY;

  push(item: T, at: number): void {
    const due = Math.max(at, this.lastAt);

    this.lastAt = due;
    this.waiting.push({ at: due, item });
  }

  /** Les trames dues à `now`, dans l'ordre. */
  due(now: number): T[] {
    let count = 0;

    while (count < this.waiting.length && this.waiting[count].at <= now) {
      count += 1;
    }

    return this.waiting.splice(0, count).map((entry) => entry.item);
  }

  /** La première trame, retirée de la file ; `null` quand rien n'attend. */
  shift(): T | null {
    const first = this.waiting.shift();

    return first === undefined ? null : first.item;
  }

  /** Toutes les trames, dans l'ordre, sans attendre. */
  drain(): T[] {
    return this.waiting.splice(0, this.waiting.length).map((entry) => entry.item);
  }

  /** La prochaine échéance, `null` quand rien n'attend. */
  get nextAt(): number | null {
    return this.waiting[0]?.at ?? null;
  }

  get size(): number {
    return this.waiting.length;
  }
}
